import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TextFieldForm } from 'web-library';
import {
  AdminProfileFormData,
  AdminProfileFormDefaultValues,
  AdminProfileIsDisableInputs,
} from '../../../../models';
import AdminNameRelatedComponent from './HelperComponents/AdminNameRelatedComponent';
import ToggleDisplayEditFormItem from '../../../../components/ToggleDisplayEditFormItem';
import { useStyles } from './styles';
import FormHeader from '../../../../components/form-items/FormHeader';
import {
  usePrCitizenshipForm,
  UsePrCitizenshipFormReturn,
} from '../../../accountManagement/logic/usePrCitizenshipForm';
import ControlledCustomSelect from '../../../../components/form-items/ControlledCustomSelect';
import ControlledDatePicker from "web-library/stories/atom/DatePicker/ControlledDatePicker";

export const ID_TYPE_MENU_ITEMS = [
  { key: 'Username', value: 'Username', children: 'CP ID' },
  { key: 'SingPass', value: 'Singpass', children: 'SingPass' },
  { key: 'MIMS', value: 'MIMS', children: 'MIMS' },
];

const getIDTypeDisplay = (idType?: string) => {
  switch (idType) {
    case 'Singpass':
      return 'SingPass';
    case 'MIMS':
      return 'MIMS';
    default:
      return 'CP ID';
  }
};

export interface AdminProfilePersonalParticularsProps {
  isEditState: boolean;
  formHook: UseFormReturn<AdminProfileFormData>;
  isDisableInputs?: AdminProfileIsDisableInputs;
  isHideExtraFormInfo?: boolean;
  formDefaultValues?: AdminProfileFormDefaultValues;
  // eslint-disable-next-line react/no-unused-prop-types
  prCitizenHook?: UsePrCitizenshipFormReturn;
  isProfileUploading?: boolean;
}

export interface ToggleDisplayEditBirthDateProps {
  isEditState: boolean;
  formHook: UseFormReturn<AdminProfileFormData>;
  isDisableInputs?: AdminProfileIsDisableInputs;
  formDefaultValues?: AdminProfileFormDefaultValues;
  isProfileUploading?: boolean;
}

const ToggleDisplayEditBirthDate = ({
  isDisableInputs,
  isEditState,
  formDefaultValues,
  formHook,
  isProfileUploading = false,
}: ToggleDisplayEditBirthDateProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { control, formState: {errors}, setValue } = formHook;
  const [dateOfBirth, setDateOfBirth] = useState<string>();
  const dateOfBirthTranslationValue = t('profile->>CreateCandidateForm.Date of Birth');

  if (isDisableInputs?.dateOfBirth && isEditState) {
    return (
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={dateOfBirthTranslationValue}
        valueText={formDefaultValues?.dateOfBirth}
        containerClassName={classes.formElementContainer}
        EditComponent={
          <TextFieldForm
              form={formHook}
            value={formDefaultValues?.dateOfBirth}
            label={dateOfBirthTranslationValue}
            name="dateOfBirth"
            type="text"
            error={!!errors.dateOfBirth}
            helperText={errors?.dateOfBirth?.message?.toString()}
            fullWidth
            inputProps={{ disabled: isDisableInputs?.dateOfBirth || isProfileUploading }}
          />
        }
      />
    );
  }
  return (
    <ToggleDisplayEditFormItem
      isEditState={isEditState}
      labelText={dateOfBirthTranslationValue}
      valueText={formDefaultValues?.dateOfBirth}
      containerClassName={classes.formElementContainer}
      EditComponent={
        <ControlledDatePicker
            control={control}
          label={dateOfBirthTranslationValue}
          onChange={(date) => {
            setDateOfBirth(date || '');
            setValue('dateOfBirth', date||'');
          }}
          inputVariant="filled"
          name="dateOfBirth"
          error={!!errors.dateOfBirth}
          helperText={errors?.dateOfBirth?.message?.toString()}
          readOnly={isDisableInputs?.dateOfBirth || isProfileUploading}
          disableFuture
        />
      }
    />
  );
};

const AdminProfilePersonalParticulars = ({
  isEditState,
  formHook,
  isDisableInputs = undefined,
  isHideExtraFormInfo = false,
  formDefaultValues = undefined,
  isProfileUploading = false,
}: AdminProfilePersonalParticularsProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { control, formState: {errors} } = formHook;

  const prCitizenHook = usePrCitizenshipForm({
    formHook,
    isDisableInputs,
    formDefaultValues,
  });

  return (
    <>
      <FormHeader
        title={t('admin->>CreateOpsAdminForm.Personal Particulars')}
        containerClassName={classes.formHeader}
      />

      <AdminNameRelatedComponent
        isEditState={isEditState}
        formHook={formHook}
        isDisableInputs={isDisableInputs}
        isHideExtraFormInfo={isHideExtraFormInfo}
        formDefaultValues={formDefaultValues}
        isProfileUploading={isProfileUploading}
      />
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={t('admin->>CreateOpsAdminForm.Id Type')}
        valueText={getIDTypeDisplay(formDefaultValues?.accountType)}
        containerClassName={`${classes.formElementContainer} ${classes.halfWidthLeftItem}`}
        EditComponent={
          formHook && (
            <ControlledCustomSelect
              id="inputSelect-idType"
              labelId="inputSelectLabel-accountType"
              label={t('admin->>CreateOpsAdminForm.Id Type')}
              name="accountType"
              control={control}
              menuItems={ID_TYPE_MENU_ITEMS}
              defaultValue={formDefaultValues?.accountType || 'Username'}
              disabled
              variant="filled"
            />
          )
        }
      />
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={t('admin->>CreateOpsAdminForm.NRIC/FIN/Passport Number')}
        valueText={formDefaultValues?.adminId}
        containerClassName={`${classes.formElementContainer} ${classes.halfWidthRightItem}`}
        EditComponent={
          <TextFieldForm
              form={formHook}
            label={t('admin->>CreateOpsAdminForm.NRIC/FIN/Passport Number')}
            name="adminId"
            type="text"
            error={!!errors.adminId}
            helperText={errors?.adminId?.message?.toString()}
            fullWidth
            inputProps={{ disabled: isDisableInputs?.adminId || isProfileUploading }}
            onChange={prCitizenHook.onIdChange}
          />
        }
      />
      <ToggleDisplayEditBirthDate
        isDisableInputs={isDisableInputs}
        isEditState={isEditState}
        formDefaultValues={formDefaultValues}
        formHook={formHook}
        isProfileUploading={isProfileUploading}
      />
    </>
  );
};
export default AdminProfilePersonalParticulars;
